import { sessionState, useAppDispatch, useAppSelector } from 'src/store';
import {
  teamsApi,
  TeamsTags,
  useAcceptInviteMutation,
  useGetTeamByInvitationTokenQuery,
} from 'src/store/services';
import { useCallback, useMemo } from 'react';
import { INVITATION_TOKEN } from 'src/constants';
import log from 'src/utils/logger';
import { toast } from 'react-toastify';
import { isTeamsTier } from 'src/utils/user';

type InvitationProps = {
  token?: string | null;
};

export const useInvitationInfo = ({ token }: InvitationProps) => {
  const {
    appUser: { user_id, tier_id },
  } = useAppSelector(sessionState);
  const dispatch = useAppDispatch();

  const [acceptInvite, { isLoading: isAcceptInviteLoading }] =
    useAcceptInviteMutation();

  const {
    data: invitationInfo,
    isLoading,
    isFetching,
    error,
  } = useGetTeamByInvitationTokenQuery(
    { token: token || '', user_id: user_id },
    {
      skip: !token || !user_id,
    },
  );

  const handleAcceptInvite = useCallback(async () => {
    try {
      await acceptInvite({
        user_id: user_id,
        token: token || '',
        team_id: invitationInfo?.team_id || '',
        seat_id: invitationInfo?.seat_id || '',
      }).unwrap();
      if (isTeamsTier(tier_id)) {
        dispatch(teamsApi.util.invalidateTags([TeamsTags.TeamByUserId]));
      }
    } catch (e) {
      log.error(e);
      toast.error('Something went wrong');
    } finally {
      localStorage.removeItem(INVITATION_TOKEN);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user_id, token, invitationInfo, acceptInvite, tier_id]);

  return useMemo(
    () => ({
      invitationInfo,
      isInvitationInfoLoading: isLoading || isFetching,
      onAcceptInvite: handleAcceptInvite,
      invitationError: error,
      isAcceptInviteLoading,
    }),
    [
      invitationInfo,
      isLoading,
      handleAcceptInvite,
      isFetching,
      error,
      isAcceptInviteLoading,
    ],
  );
};
